import React from 'react'
import { useIntl } from 'react-intl'
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import { BasicTable } from '../basic-table'

/**
 * @typedef {import('../basic-table/basic-table.component').ColumnConfig} ColumnConfig
 */

/**
 * @typedef {object} DocumentViewAccordionComponentProps
 * @property {object[]} data - El array de objetos de datos que se pasarán al `BasicTable`.
 * @property {string} title - El ID del mensaje para `react-intl` que se usará como título del acordeón.
 * @property {ColumnConfig[]} config - El array de configuración de columnas para el `BasicTable`.
 * @property {function(object): void} [onClick] - La función que se ejecuta al hacer clic en una fila de la tabla. Se pasa directamente a `BasicTable`.
 * @property {boolean} [loading] - Un flag para indicar el estado de carga, que se pasa al `BasicTable` para aplicar estilos.
 */

/**
 * Un componente que presenta datos tabulares dentro de un acordeón colapsable de Material-UI.
 *
 * Utiliza un `BasicTable` para renderizar los datos y `react-intl` para internacionalizar el título del acordeón.
 * Es ideal para organizar y mostrar secciones de datos que el usuario puede expandir o contraer para
 * mejorar la legibilidad de la interfaz.
 *
 * @component
 * @example
 * const documentsData = [
 * { id: 'doc1', name: 'Contrato.pdf', uploadDate: '2025-10-20T10:00:00Z' },
 * { id: 'doc2', name: 'Factura.pdf', uploadDate: '2025-10-19T11:30:00Z' },
 * ];
 *
 * const documentsConfig = [
 * { label: 'Nombre del Archivo', key: 'name' },
 * { label: 'Fecha de Subida', key: 'uploadDate', type: 'datetime' },
 * ];
 *
 * const handleDocumentClick = (doc) => {
 * console.log('Abriendo documento:', doc.name);
 * };
 *
 * // Este componente debe estar envuelto en un <IntlProvider> y los IDs
 * // como 'documents.section.title' deben estar definidos en los mensajes.
 * <DocumentViewAccordionComponent
 * title="documents.section.title"
 * data={documentsData}
 * config={documentsConfig}
 * onClick={handleDocumentClick}
 * loading={false}
 * />
 *
 * @param {DocumentViewAccordionComponentProps} props - Las props del componente DocumentViewAccordionComponent.
 */
export const DocumentViewAccordionComponent = ({ data, title, config, onClick, loading }) => {
  const { formatMessage } = useIntl()

  return (
    <Box mb={4}>
      <Accordion style={{ padding: 0 }}>
        <AccordionSummary style={{ padding: 0 }} color="primary" expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" color="primary" paragraph>
            {formatMessage({
              id: title,
            })}
          </Typography>
        </AccordionSummary>
        <AccordionDetails style={{ padding: 0 }}>
          <BasicTable config={config} data={data} onClick={onClick} loading={loading} />
        </AccordionDetails>
      </Accordion>
    </Box>
  )
}
